import { Button, Tooltip, Input, Popover, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useGlobalContext } from '@src/context/globalContext';
// import { PlayCircleOutlined, PauseCircleOutlined, EditOutlined } from '@ant-design/icons';
import { getVideoPreview } from 'polotno/utils/video';
import './timeline.less';
import { PlayPauseButton } from '@src/features/video/player/playPause'
import { usePlayer } from '@src/context/playerContext'
import { useCurrentPlayerFrame } from '@src/features/video/player/useCurrentFramePlayer';
import Draggable, { DraggableCore } from 'react-draggable';
import { SoundOutlined, CustomerServiceOutlined, ThunderboltFilled, CopyOutlined, AudioMutedOutlined, DeleteOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

// Helper function to format time
const formatTime = (ms: number) => {
  const seconds = ms / 1000;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toFixed(1).padStart(4, '0')}`;
};

// Add this type for better type safety
type Word = {
  word: string;
  start: number;
  end: number;
};

// Add SFX types after the Word type
type SFX = {
  url: string;
  duration: number;
  start: number;
  end: number;
  align: string;
  volume: number;
  name: string;
  section: number;
  id: string;
};

interface Page {
  id: string;
  duration: number;
  custom?: {
    scriptIndex: number;
  };
}

// Add interface for JsonPages at the top of the file
interface JsonPages {
  pages: any[];
  [key: string]: any;
}

// Add these styles to prevent text selection during drag
const styles = `
.timeline-container {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.scene-dragger {
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.dragging * {
  cursor: ew-resize !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}

.caption-block {
  position: absolute;
  height: 26px;
  padding: 3px 6px;
  border-radius: 3px;
  cursor: move;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  transition: background-color 0.2s ease;
  box-sizing: border-box;
}

.caption-block:hover {
  z-index: 5;
}



.caption-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  background-color: rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.caption-block:hover .caption-resize-handle {
  opacity: 1;
}

.caption-resize-handle-left {
  left: 0;
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
}

.caption-resize-handle-right {
  right: 0;
  border-top-right-radius: 3px;
  border-bottom-right-radius: 3px;
}

.sfx-track {
  position: relative;
  height: 40px;
  margin-top: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.sfx-track.drag-over {
  background-color: var(--color-primary-50);
}

.sfx-block {
  position: absolute;
  height: 26px;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: move;
  overflow: visible;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  background: var(--color-gray-100);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: 4px;
  top: 7px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  will-change: transform, left, width;
  transform: translateZ(0);
  min-width: 16px;
}

.sfx-block.dragging {
  transition: none;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  opacity: 0.9;
  pointer-events: none;
}

.sfx-block:hover {
  z-index: 5;
  background: var(--color-gray-200);
  border-color: var(--color-border-strong);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.sfx-block.active-sfx {
  background: var(--color-primary-100);
  border-color: var(--color-primary-500);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.sfx-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--color-primary-500);
  background: var(--color-primary-100);
  width: 18px;
  height: 18px;
  border-radius: 3px;
  margin-right: 2px;
}

.sfx-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: var(--color-primary-700);
}

.sfx-wave {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 24px;
  opacity: 0.15;
  background: repeating-linear-gradient(
    45deg,
    var(--color-primary-300) 0px,
    var(--color-primary-300) 2px,
    transparent 2px,
    transparent 6px
  );
}

.sfx-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize !important;
  background-color: var(--color-primary-400);
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.sfx-block:hover .sfx-resize-handle {
  opacity: 0.4;
}

.sfx-block:hover .sfx-resize-handle:hover {
  opacity: 0.8;
  background-color: var(--color-primary-500);
}

.sfx-resize-handle-left {
  left: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.sfx-resize-handle-right {
  right: 0;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.sfx-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  position: relative;
  z-index: 10;
}

.sfx-block:hover .sfx-controls {
  opacity: 1;
}

.sfx-control-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background: var(--color-primary-100);
  border: 1px solid var(--color-primary-200);
  color: var(--color-primary-600);
  font-size: 10px;
  transition: all 0.2s ease;
  cursor: pointer !important;
  position: relative;
  z-index: 20;
}

.sfx-control-button .anticon {
  cursor: pointer !important;
  pointer-events: none;
}

.sfx-control-button:hover {
  background: var(--color-primary-200);
  border-color: var(--color-primary-300);
  color: var(--color-primary-700);
}

.sfx-control-button.muted {
  background: var(--color-error-100);
  border-color: var(--color-error-200);
  color: var(--color-error-600);
}

.sfx-control-button.muted:hover {
  background: var(--color-error-200);
  border-color: var(--color-error-300);
  color: var(--color-error-700);
}

.sfx-block {
  cursor: move;
  * {
    cursor: inherit;
  }
  .sfx-controls, .sfx-control-button {
    cursor: default;
  }
  .sfx-control-button {
    cursor: pointer !important;
  }
}

.timeline-section {
  border: 1px solid transparent;
  border-radius: 4px;
  margin: 4px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.timeline-section.active-section {
  background-color: var(--color-surface);
  border-color: var(--color-border);
}

.timeline-section[data-active="true"] {
  z-index: 2;
}

.timeline-content {
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.timeline-sections {
  display: flex;
  flex-direction: row;
  min-width: 100%;
  position: relative;
}

.timeline-section {
  flex: 0 0 auto;
  min-width: 300px; /* Ensure sections have a minimum width */
  max-width: none;
  position: relative;
  scroll-margin: 50px; /* Add some margin for smoother scrolling */
}
`;

// Add styles to document
const styleSheet = document.createElement('style');
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

// Add these new interfaces and types at the top
interface DragState {
  startX: number;
  initialDuration: number;
  isDragging: boolean;
}

interface CaptionDragState {
  word: Word;
  index: number;
  sectionIndex: number;
  initialStart: number;
  initialEnd: number;
  isDragging: boolean;
}

type MediaChild = {
  type: 'image' | 'video';
  src: string;
  id: string;
};

const TimelineTab = ({ store }: any) => {
  const [jsonPages, setJsonPages] = useState<any>({});
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [editableWordIndex, setEditableWordIndex] = useState<number | null>(null);
  const [editedWord, setEditedWord] = useState('');
  const [imagePreviews, setImagePreviews] = useState<{ [key: string]: string }>({});
  const timelineRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [editingCaption, setEditingCaption] = useState<{
    index: number;
    word: string;
    sectionIndex: number;
  } | null>(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);

  const [editedCaption, setEditedCaption] = useState('');
  const [captionWarning, setCaptionWarning] = useState('');
  const playerRef = usePlayer();
  const {
    ScriptDetails: [scriptDetails, setScriptDetails],
    SelectedSectionIndex: [selectedSectionIndex, setSelectedSectionIndex],
    CurrentFrame: [globalCurrentFrame, setGlobalCurrentFrame]
  } = useGlobalContext();
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [isPlayerInitialized, setIsPlayerInitialized] = useState(false);
  const playerInstanceRef = useRef<any>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartPosition, setDragStartPosition] = useState(0);
  // State to track playhead hover
  const [isPlayheadHovered, setIsPlayheadHovered] = useState(false);
  const dragRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isDraggingRef = useRef<{ [key: string]: boolean }>({});
  const dragStartPositionRef = useRef<{ [key: string]: { x: number, duration: number } }>({});
  const [captionDragState, setCaptionDragState] = useState<CaptionDragState | null>(null);
  const [isResizingCaption, setIsResizingCaption] = useState(false);
  const captionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [lockedSectionIndex, setLockedSectionIndex] = useState<number | null>(null);

  // Add new state for SFX dragging
  const [sfxDragState, setSfxDragState] = useState<{
    sfx: SFX;
    index: number;
    sectionIndex: number;
    initialStart: number;
    initialEnd: number;
    isDragging: boolean;
  } | null>(null);

  const [isResizingSfx, setIsResizingSfx] = useState(false);
  const sfxRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Update functions to use standardized color variables
  const getPrimaryColor = () => 'var(--color-primary-500)';
  const getPrimaryColorHover = () => 'var(--color-primary-600)';
  const getPrimaryColorLight = () => 'var(--color-primary-200)';
  const getPrimaryColorDark = () => 'var(--color-primary-700)';

  // Add this near the beginning of the component, after state declarations
  const safeSetActiveSectionIndex = (newIndex: number) => {
    // Block ALL section changes while dragging the playhead
    if (isDraggingPlayhead) {
      console.warn(`Blocked section change to ${newIndex} during playhead dragging`);
      return;
    }

    // Only update if changed
    if (newIndex !== activeSectionIndex) {
      setActiveSectionIndex(newIndex);
    }
  };

  useEffect(() => {
    const checkPlayingState = () => {
      const currentPlayer = playerRef.current;
      if (!currentPlayer) return;

      try {

        setCurrentFrame(currentPlayer.getCurrentFrame());
      } catch (error) {
        console.error('Error checking playing state:', error);
      }
    };

    // Initial check
    checkPlayingState();

    // Check state every 100ms
    const interval = setInterval(checkPlayingState, 100);

    return () => {
      clearInterval(interval);
    };
  }, [playerRef]);

  // Track page count to refresh timeline when pages are added/removed
  const pagesCount = store?.pages?.length || 0;

  useEffect(() => {
    setJsonPages(store.toJSON());
  }, [pagesCount, store]);

  useEffect(() => {
    if (store?.activePage?.id) {
      setJsonPages(store.toJSON());
    }
  }, [store, store.activePage]);


  useEffect(() => {
    const fetchImagePreviews = async () => {
      if (!jsonPages?.pages) return;

      // Default fallback URLs for different media types
      const fallbackImageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjBmMGYwIi8+CjxwYXRoIGQ9Im0xNSAxMi00IDMtNC0zVjZoOHY2WiIgZmlsbD0iIzk5OTk5OSIvPgo8L3N2Zz4K';
      const fallbackVideoUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjBmMGYwIi8+CjxwYXRoIGQ9Im0xMCA4IDUtMyAyIDEuNVYxOGwtMiAxLjUtNS0zVjhaIiBmaWxsPSIjOTk5OTk5Ii8+Cjwvc3ZnPgo=';
      
      // Initialize previews with fallback URLs immediately
      const initialPreviews: { [key: string]: string } = {};
      const loadingTasks: Promise<void>[] = [];
      
      for (const page of jsonPages.pages) {
        let mediaChild: MediaChild | null = null;
        let isVideo = false;
        
        // Find the first media child (image or video)
        for (const child of page.children) {
          if (child.type === 'image') {
            mediaChild = child as MediaChild;
            isVideo = false;
            break;
          } else if (child.type === 'video') {
            mediaChild = child as MediaChild;
            isVideo = true;
            break;
          }
        }
        
        if (mediaChild) {
          if (isVideo) {
            // Set fallback for video immediately
            initialPreviews[page.id] = fallbackVideoUrl;
            
            // Add async task to load actual video preview
            const loadVideoPreview = async () => {
              try {
                if (!mediaChild) {
                  throw new Error('No media child found');
                }
                const imagePreview = await getVideoPreview(mediaChild.src);
                setImagePreviews(prev => ({
                  ...prev,
                  [page.id]: imagePreview
                }));
              } catch (error) {
                if (mediaChild) {
                  console.warn('Failed to generate video preview for:', mediaChild.src, error);
                } else {
                  console.warn('Failed to generate video preview: No media child found', error);
                }
                // Keep the fallback URL on error
              }
            };
            
            loadingTasks.push(loadVideoPreview());
          } else {
            // For images, use the actual source immediately
            initialPreviews[page.id] = mediaChild.src;
          }
        } else {
          // No media found, use generic fallback
          initialPreviews[page.id] = fallbackImageUrl;
        }
      }
      
      // Set initial previews immediately (with fallbacks for videos)
      setImagePreviews(initialPreviews);
      
      // Load video previews in parallel without blocking
      if (loadingTasks.length > 0) {
        // Execute all video preview generations in parallel
        Promise.allSettled(loadingTasks).then((results) => {
          // Log any failures for debugging, but don't block the UI
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.warn('Video preview generation failed:', result.reason);
            }
          });
        });
      }
    };

    fetchImagePreviews();
  }, [jsonPages]);


  const groupPagesByScriptIndex = (pages: any[]) => {
    return pages.reduce((groups: any, page: any) => {
      const scriptIndex = page.custom?.scriptIndex || 0;
      if (!groups[scriptIndex]) {
        groups[scriptIndex] = [];
      }
      groups[scriptIndex].push(page);
      return groups;
    }, {});
  };

  // Initialize drag refs when pages change
  useEffect(() => {
    if (jsonPages?.pages) {
      const newRefs: { [key: string]: HTMLDivElement | null } = {};
      jsonPages.pages.forEach((page: any) => {
        newRefs[page.id] = null;
      });
      dragRefs.current = newRefs;
    }
  }, [jsonPages?.pages]);

  const setDragRef = (pageId: string, element: HTMLDivElement | null) => {
    if (dragRefs.current) {
      dragRefs.current[pageId] = element;
    }
  };

  // Cleanup effect at the top level
  useEffect(() => {
    return () => {
      // Cleanup any ongoing drags when component unmounts
      Object.keys(isDraggingRef.current).forEach(pageId => {
        if (isDraggingRef.current[pageId]) {
          handleDragStop(pageId, 0);
        }
      });
    };
  }, []);

  const handleDragStart = (pageId: string) => {
    if (!dragRefs.current[pageId]) return;

    const page = store.pages.find((p: any) => p.id === pageId);
    if (!page) return;

    isDraggingRef.current[pageId] = true;
    dragStartPositionRef.current[pageId] = {
      x: 0,
      duration: page.duration
    };

    try {
      document.body.setAttribute('data-dragging', 'true');
    } catch (err) {
      console.error('Error setting dragging attribute:', err);
    }
  };

  const handleDragStop = (pageId: string, finalX: number) => {
    if (!dragRefs.current[pageId]) return;

    const startPosition = dragStartPositionRef.current[pageId];
    if (!startPosition) return;

    isDraggingRef.current[pageId] = false;

    try {
      document.body.removeAttribute('data-dragging');
    } catch (err) {
      console.error('Error removing dragging attribute:', err);
    }

    try {
      // Calculate new duration based on drag distance
      const durationChange = Math.round(finalX / 5) * 25;
      const newDuration = Math.max(500, startPosition.duration + durationChange);

      // Update the page duration
      const pageToUpdate = store.pages.find((page: any) => page.id === pageId);
      if (!pageToUpdate) return;

      pageToUpdate.set({
        duration: newDuration,
      });

      // Update store and recalculate frames
      setJsonPages(store.toJSON());

      // Update player position
      const sectionPages = groupPagesByScriptIndex(store.toJSON().pages)[activeSectionIndex] || [];
      const sectionStartTime = calculateSectionDuration(activeSectionIndex).startTime;
      const pageIndex = sectionPages.findIndex((page: any) => page.id === pageId);

      if (pageIndex !== -1) {
        let accumulatedTime = sectionStartTime;
        for (let i = 0; i < pageIndex; i++) {
          accumulatedTime += sectionPages[i].duration;
        }

        const newTimeMs = accumulatedTime + (newDuration / 2);
        const newFrame = Math.round((newTimeMs / 1000) * 30);

        if (playerRef.current) {
          playerRef.current.seekTo(newFrame);
          setCurrentFrame(newFrame);
          setGlobalCurrentFrame(newFrame);
        }
      }
    } catch (error) {
      console.error('Error during drag stop:', error);
    }
  };

  const handleCaptionClick = (index: number, word: string, sectionIndex: number) => {
    setEditingCaption({ index, word, sectionIndex });
    setEditedCaption(word);
    setCaptionWarning('');
  };

  const validateCaptionLength = (newWord: string, originalWord: string) => {
    const originalLength = originalWord.length;
    const newLength = newWord.length;
    const lengthDifference = Math.abs(newLength - originalLength);

    if (lengthDifference > originalLength * 0.5) { // 50% difference threshold
      setCaptionWarning('Warning: The new word length differs significantly from the original. This might affect timing.');
      return false;
    }
    setCaptionWarning('');
    return true;
  };

  const handleCaptionUpdate = () => {
    if (!editingCaption) return;

    const { index, sectionIndex } = editingCaption;
    // const originalWord = scriptDetails?.data[sectionIndex]?.captions?.[0]?.words[index]?.word;

    // if (!validateCaptionLength(editedCaption, originalWord)) {
    //   return;
    // }

    const updatedWords = [...scriptDetails.data[sectionIndex].captions[0].words];
    updatedWords[index] = {
      ...updatedWords[index],
      word: editedCaption
    };

    setScriptDetails((prevScript: any) => {
      const updatedScript = { ...prevScript };
      const updatedData = [...prevScript.data];
      updatedData[sectionIndex] = {
        ...updatedData[sectionIndex],
        captions: [{ words: updatedWords }]
      };
      return { ...updatedScript, data: updatedData };
    });

    setEditingCaption(null);
    setEditedCaption('');
    setCaptionWarning('');
    message.success('Caption updated successfully');
  };
  
  const handleCaptionDelete = () => {
    if (!editingCaption) return;

    const { index, sectionIndex } = editingCaption;

    const updatedWords = [...scriptDetails.data[sectionIndex].captions[0].words];
    // Remove the word at the specified index
    updatedWords.splice(index, 1);

    setScriptDetails((prevScript: any) => {
      const updatedScript = { ...prevScript };
      const updatedData = [...prevScript.data];
      updatedData[sectionIndex] = {
        ...updatedData[sectionIndex],
        captions: [{ words: updatedWords }]
      };
      return { ...updatedScript, data: updatedData };
    });

    setEditingCaption(null);
    setEditedCaption('');
    setCaptionWarning('');
    message.success('Caption deleted successfully');
  };

  const handleCaptionCancel = () => {
    setEditingCaption(null);
    setEditedCaption('');
    setCaptionWarning('');
  };

  const captionEditContent = (
    <div style={{ width: 300 }}>
      <Input
        value={editedCaption}
        onChange={(e) => {
          setEditedCaption(e.target.value);
          if (editingCaption) {
            validateCaptionLength(e.target.value, editingCaption.word);
          }
        }}
        placeholder="Edit caption"
        style={{ marginBottom: 8 }}
      />
      {captionWarning && (
        <div style={{ color: '#faad14', marginBottom: 8 }}>
          {captionWarning}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={handleCaptionCancel}>Cancel</Button>
        <Button onClick={handleCaptionDelete}>Delete</Button>
        <Button type="primary" onClick={handleCaptionUpdate}>
          Update
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    if (jsonPages?.pages) {
      const total = jsonPages.pages.reduce((sum: number, page: any) => sum + page.duration, 0);
      setTotalDuration(total);
    }
  }, [jsonPages]);

  // Update the renderTimeScale function to provide more markers for longer durations
  const renderTimeScale = (startTime: number, endTime: number, isActiveSection: boolean) => {
    const markers = [];
    const sectionDuration = endTime - startTime;
    const totalSeconds = Math.ceil(sectionDuration / 1000);

    // Calculate minimum spacing between markers (in pixels)
    // Use more markers for longer durations
    const minSpacing = 35; // Reduced minimum spacing to fit more markers
    const totalWidth = 100; // Total width in percentage

    // Increase max markers for longer durations
    const baseMaxMarkers = Math.floor(totalWidth / (minSpacing / 100));
    const durationFactor = Math.max(1, Math.min(2, totalSeconds / 20)); // Scale factor based on duration
    const maxMarkers = Math.floor(baseMaxMarkers * durationFactor);

    // Adjust interval based on section duration and max markers
    const interval = Math.max(500, Math.ceil(sectionDuration / maxMarkers)); // Reduced from 1000ms to 500ms

    // Calculate the number of markers that will fit
    const numMarkers = Math.min(
      Math.ceil(sectionDuration / interval),
      maxMarkers
    );

    // Calculate the actual interval to ensure even spacing
    const adjustedInterval = sectionDuration / numMarkers;

    // Always include start and end markers
    for (let i = 0; i <= numMarkers; i++) {
      const timeMs = i * adjustedInterval;
      const position = (timeMs / sectionDuration) * 100;

      // Determine if this is the first or last marker
      const isFirstMarker = i === 0;
      const isLastMarker = i === numMarkers;

      // Add special classes for first and last markers
      const markerClass = `time-marker ${isFirstMarker ? 'marker-start' : ''} ${isLastMarker ? 'marker-end' : ''}`;

      markers.push(
        <div
          key={`marker-${i}`}
          className={markerClass}
          style={{
            left: `${position}%`,
            color: isActiveSection ? getPrimaryColorDark() : 'rgba(0, 0, 0, 0.65)',
            fontWeight: isActiveSection && (isFirstMarker || isLastMarker) ? 700 : (isActiveSection ? 500 : 400),
            fontSize: (isFirstMarker || isLastMarker) ? '11px' : '10px'
          }}
        >
          {formatTime(timeMs + startTime)}
        </div>
      );
    }

    // Make sure first and last markers are properly positioned
    markers[0] = (
      <div
        key="marker-start"
        className="time-marker marker-start"
        style={{
          left: '0%',
          color: isActiveSection ? getPrimaryColorDark() : '#222',
          fontWeight: 700,
          fontSize: '11px'
        }}
      >
        {formatTime(startTime)}
      </div>
    );

    markers[markers.length - 1] = (
      <div
        key="marker-end"
        className="time-marker marker-end"
        style={{
          left: '100%',
          color: isActiveSection ? getPrimaryColorDark() : '#222',
          fontWeight: 700,
          fontSize: '11px'
        }}
      >
        {formatTime(endTime)}
      </div>
    );

    return <div className="time-scale">{markers}</div>;
  };

  const getWordsWithinDuration = (words: Word[], duration: number) => {
    return words.filter(word => word.end <= duration / 1000);
  };

  // Calculate duration from pages
  const calculateSectionDuration = (sectionIndex: number) => {
    const pages = store.toJSON().pages;
    const sectionPages = pages.filter((page: any) => page?.custom?.scriptIndex === sectionIndex);

    if (sectionPages.length === 0) return { startTime: 0, endTime: 0 };

    // Calculate start time by summing durations of previous sections
    let startTime = 0;
    for (let i = 0; i < sectionIndex; i++) {
      const prevSectionPages = pages.filter((page: any) => page?.custom?.scriptIndex === i);
      startTime += prevSectionPages.reduce((sum: number, page: any) => sum + (page.duration || 0), 0);
    }

    // Calculate end time by adding current section duration
    const currentSectionDuration = sectionPages.reduce((sum: number, page: any) => sum + (page.duration || 0), 0);
    const endTime = startTime + currentSectionDuration;

    return { startTime, endTime };
  };

  const getPlayheadPosition = () => {
    if (!totalDuration || !jsonPages?.pages) return 0;

    const currentTimeMs = (currentFrame / 30) * 1000;

    // When we're dragging, ALWAYS use the active section
    if (isDraggingPlayhead) {
      console.debug("DRAGGING: Locked to section", activeSectionIndex);
      const { startTime, endTime } = calculateSectionDuration(activeSectionIndex);
      const sectionDuration = endTime - startTime;
      let positionInSection = currentTimeMs - startTime;
      positionInSection = Math.max(0, Math.min(sectionDuration, positionInSection));
      const sectionPosition = (positionInSection / sectionDuration) * 100;
      return Math.min(100, Math.max(0, sectionPosition));
    }

    // Find correct section based on current time
    let correctSectionIndex = 0;
    let accumulatedTime = 0;

    const sections = groupPagesByScriptIndex(jsonPages.pages);
    for (let i = 0; i < Object.keys(sections).length; i++) {
      const sectionPages = sections[i] || [];
      const sectionDuration = sectionPages.reduce((sum: number, page: any) => sum + page.duration, 0);

      if (currentTimeMs >= accumulatedTime && currentTimeMs < accumulatedTime + sectionDuration) {
        correctSectionIndex = i;
        console.debug(`Time ${currentTimeMs}ms falls in section ${i} (${accumulatedTime}ms - ${accumulatedTime + sectionDuration}ms)`);
        break;
      }
      accumulatedTime += sectionDuration;
    }

    // If section changed, force a scroll
    if (correctSectionIndex !== activeSectionIndex) {
      console.debug(`Section change detected: ${activeSectionIndex} -> ${correctSectionIndex}`);
      safeSetActiveSectionIndex(correctSectionIndex);
      setSelectedSectionIndex(correctSectionIndex);

      // Force scroll to the new section
      const sectionElement = document.querySelector(`[data-section-index="${correctSectionIndex}"]`);
      if (sectionElement && timelineRef.current) {
        sectionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }

    // Calculate position within current section
    const { startTime, endTime } = calculateSectionDuration(correctSectionIndex);
    const sectionDuration = endTime - startTime;
    const positionInSection = currentTimeMs - startTime;
    const sectionPosition = (positionInSection / sectionDuration) * 100;

    return Math.min(100, Math.max(0, sectionPosition));
  };

  // Update the scrolling effect to ensure active section is visible
  useEffect(() => {
    if (!timelineRef.current || activeSectionIndex === undefined) return;

    // Skip scrolling if currently dragging the playhead
    if (isDraggingPlayhead) return;

    console.debug(`Ensuring section ${activeSectionIndex} is visible`);

    // Find the active section element
    const sectionElement = document.querySelector(`[data-section-index="${activeSectionIndex}"]`) as HTMLElement;
    if (!sectionElement) {
      console.warn(`Could not find section element for index ${activeSectionIndex}`);
      return;
    }

    const timelineElement = timelineRef.current;
    const timelineRect = timelineElement.getBoundingClientRect();
    const sectionRect = sectionElement.getBoundingClientRect();

    // Calculate if section is in view
    const isInView = (
      sectionRect.left >= timelineRect.left &&
      sectionRect.right <= timelineRect.right
    );



    if (!isInView) {
      // Calculate the target scroll position to center the section
      const currentScroll = timelineElement.scrollLeft;
      const sectionLeft = sectionRect.left - timelineRect.left + currentScroll;
      const targetScroll = sectionLeft - (timelineRect.width - sectionRect.width) / 2;



      // Scroll the timeline container
      timelineElement.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });

      // Backup scroll if needed
      setTimeout(() => {
        const newSectionRect = sectionElement.getBoundingClientRect();
        const newTimelineRect = timelineElement.getBoundingClientRect();

        if (newSectionRect.left < newTimelineRect.left || newSectionRect.right > newTimelineRect.right) {
          console.debug('Section still not fully visible, using scrollIntoView');
          sectionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 300);
    }
  }, [activeSectionIndex, isDraggingPlayhead]);

  // Add this new effect to handle section visual updates
  useEffect(() => {
    if (activeSectionIndex === undefined) return;

    console.debug(`Updating visual state for section ${activeSectionIndex}`);

    // Update section styling
    const allSections = document.querySelectorAll('.timeline-section');
    allSections.forEach((section, index) => {
      const isActive = index === activeSectionIndex;
      section.classList.toggle('active-section', isActive);
      if (isActive) {
        section.setAttribute('data-active', 'true');
      } else {
        section.removeAttribute('data-active');
      }
    });

  }, [activeSectionIndex]);

  // Effect to track playhead position and active section
  useEffect(() => {
    if (!isPlayerInitialized || !playerInstanceRef.current || !timelineRef.current) return;
    if (isDraggingPlayhead) return;

    const player = playerInstanceRef.current;

    const findSectionForTime = (timeMs: number) => {
      if (!jsonPages?.pages) return -1;

      let accumulatedTime = 0;
      const sections = groupPagesByScriptIndex(jsonPages.pages);

      for (let i = 0; i < Object.keys(sections).length; i++) {
        const sectionPages = sections[i] || [];
        const sectionDuration = sectionPages.reduce((sum: number, page: any) => sum + page.duration, 0);



        // Check if time falls within this section
        if (timeMs >= accumulatedTime && timeMs < accumulatedTime + sectionDuration) {
          return i;
        }
        accumulatedTime += sectionDuration;
      }

      // If we're past the last section, return the last section index
      if (Object.keys(sections).length > 0 && timeMs >= accumulatedTime) {
        return Object.keys(sections).length - 1;
      }

      return -1;
    };

    const handleFrameUpdate = () => {
      try {
        const frame = player.getCurrentFrame();
        const currentTimeMs = (frame / 30) * 1000;

        // Find which section we should be in
        const currentSection = findSectionForTime(currentTimeMs);


        // Only update and scroll if we've moved to a different section
        if (currentSection !== -1 && currentSection !== activeSectionIndex) {

          // First update the active section
          safeSetActiveSectionIndex(currentSection);
          setSelectedSectionIndex(currentSection);

          // Then find and scroll to the section element
          const sectionElement = document.querySelector(`[data-section-index="${currentSection}"]`);
          if (sectionElement && timelineRef.current) {
            // Get the timeline container's scroll position
            const timelineRect = timelineRef.current.getBoundingClientRect();
            const sectionRect = sectionElement.getBoundingClientRect();

            // Calculate how far we need to scroll
            const scrollLeft = timelineRef.current.scrollLeft + (sectionRect.left - timelineRect.left);

            // Scroll to the section
            timelineRef.current.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });

            // Update active section styling
            const allSections = document.querySelectorAll('.timeline-section');
            allSections.forEach(section => section.removeAttribute('data-active'));
            sectionElement.setAttribute('data-active', 'true');


          } else {
            console.warn('Could not find section element:', currentSection);
          }
        }

        // Update frames
        setCurrentFrame(frame);
        setGlobalCurrentFrame(frame);
      } catch (error) {
        console.error('Error tracking playhead position:', error);
      }
    };

    // Add event listeners
    player.addEventListener('frameupdate', handleFrameUpdate);
    player.addEventListener('play', handleFrameUpdate);
    player.addEventListener('pause', handleFrameUpdate);
    player.addEventListener('seeked', handleFrameUpdate);

    // Initial check
    handleFrameUpdate();

    return () => {
      player.removeEventListener('frameupdate', handleFrameUpdate);
      player.removeEventListener('play', handleFrameUpdate);
      player.removeEventListener('pause', handleFrameUpdate);
      player.removeEventListener('seeked', handleFrameUpdate);
    };
  }, [isPlayerInitialized, jsonPages, activeSectionIndex, isDraggingPlayhead]);

  // Keep a separate effect just for playhead visibility
  useEffect(() => {
    if (!timelineRef.current || activeSectionIndex === undefined || !playheadRef.current) return;

    // Skip if dragging
    if (isDraggingPlayhead) return;

    // Get the playhead and timeline positions
    const playheadRect = playheadRef.current.getBoundingClientRect();
    const timelineRect = timelineRef.current.getBoundingClientRect();

    // Check if playhead is offscreen
    const isPlayheadOffscreen =
      playheadRect.right > timelineRect.right ||
      playheadRect.left < timelineRect.left;

    if (isPlayheadOffscreen) {
      const sectionElement = document.querySelector(`[data-section-index="${activeSectionIndex}"]`);
      if (!sectionElement) return;

      // Only scroll if playhead is offscreen
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [activeSectionIndex, currentFrame, isDraggingPlayhead]);

  const isCaptionActive = (word: any, previousSectionsDuration: number, totalSectionDuration: number) => {
    const currentTimeMs = (currentFrame / 30) * 1000;
    const wordStartTime = previousSectionsDuration + (word.start * 1000);
    const wordEndTime = previousSectionsDuration + (word.end * 1000);

    return currentTimeMs >= wordStartTime && currentTimeMs <= wordEndTime;
  };

  const handleTimelineClick = (e: React.MouseEvent, sectionIndex: number) => {
    // Don't respond to clicks when dragging
    if (isDraggingPlayhead) return;

    console.debug(`Timeline clicked: section=${sectionIndex}`);

    const sectionElement = e.currentTarget as HTMLElement;
    const rect = sectionElement.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercentage = (clickX / rect.width) * 100;

    // Calculate total time up to this section
    let totalStartTime = 0;
    for (let i = 0; i < sectionIndex; i++) {
      const prevSectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
      totalStartTime += prevSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0);
    }

    // Calculate time within this section
    const { startTime, endTime } = calculateSectionDuration(sectionIndex);
    const sectionDuration = endTime - startTime;
    const clickTimeMs = totalStartTime + ((clickPercentage / 100) * sectionDuration);
    const newFrame = Math.round((clickTimeMs / 1000) * 30);

    console.debug(`Click position: section=${sectionIndex}, time=${clickTimeMs}ms, frame=${newFrame}`);

    if (playerRef.current) {
      playerRef.current.seekTo(newFrame);
      setCurrentFrame(newFrame);
      setGlobalCurrentFrame(newFrame);

      // Update both section indices
      safeSetActiveSectionIndex(sectionIndex);
      setSelectedSectionIndex(sectionIndex);
    }
  };

  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Set locked section to current active section
    setLockedSectionIndex(activeSectionIndex);

    // Start dragging
    setIsDraggingPlayhead(true);
    setIsPlayheadHovered(true);
    setDragStartX(e.clientX);
  };

  const handlePlayheadMouseMove = (e: React.MouseEvent | MouseEvent) => {
    if (!isDraggingPlayhead || !timelineRef.current) return;

    // Use the locked section index if available, otherwise use active section
    const sectionIndex = lockedSectionIndex !== null ? lockedSectionIndex : activeSectionIndex;

    // Don't change sections while dragging - only use the locked section
    const sectionElement = document.querySelector(`[data-section-index="${sectionIndex}"]`);
    if (!sectionElement) return;

    // Get mouse position
    const mouseX = 'clientX' in e ? e.clientX : (e as MouseEvent).clientX;
    const rect = sectionElement.getBoundingClientRect();

    // Calculate position within the locked section only (no section switching)
    // Make sure we clamp to section boundaries
    let relativeX = mouseX - rect.left;
    if (relativeX < 0) relativeX = 0;
    if (relativeX > rect.width) relativeX = rect.width;

    const positionPercentage = (relativeX / rect.width) * 100;

    // Calculate the frame based on the position
    const { startTime, endTime } = calculateSectionDuration(sectionIndex);
    const sectionDuration = endTime - startTime;
    const positionInSection = (Math.min(100, Math.max(0, positionPercentage)) / 100) * sectionDuration;
    const newTimeMs = startTime + positionInSection;
    const newFrame = Math.round((newTimeMs / 1000) * 30);

    console.debug(`Dragging in section ${sectionIndex}: pos=${positionPercentage.toFixed(2)}%, frame=${newFrame}`);

    // Update player position
    if (playerRef.current) {
      playerRef.current.seekTo(newFrame);
      setCurrentFrame(newFrame);
      setGlobalCurrentFrame(newFrame);
    }
  };

  const handlePlayheadMouseUp = (e: React.MouseEvent | MouseEvent) => {
    if (!isDraggingPlayhead) return;

    // Use locked section index if available
    const sectionIndex = lockedSectionIndex !== null ? lockedSectionIndex : activeSectionIndex;
    console.debug(`Mouse up event while dragging playhead in section ${sectionIndex}`);

    // Get current section element - use LOCKED section, not mouse position
    const currentSectionElement = document.querySelector(`[data-section-index="${sectionIndex}"]`);
    if (currentSectionElement) {
      // Get current mouse position
      const mouseX = 'clientX' in e ? e.clientX : (e as MouseEvent).clientX;
      const rect = currentSectionElement.getBoundingClientRect();

      // Calculate position within the section - clamped to section boundaries
      let relativeX = mouseX - rect.left;
      if (relativeX < 0) relativeX = 0;
      if (relativeX > rect.width) relativeX = rect.width;

      const positionPercentage = (relativeX / rect.width) * 100;

      // Calculate time based on position in THIS section
      const { startTime, endTime } = calculateSectionDuration(sectionIndex);
      const sectionDuration = endTime - startTime;
      const positionInSection = (positionPercentage / 100) * sectionDuration;
      const newTimeMs = startTime + positionInSection;
      const newFrame = Math.round((newTimeMs / 1000) * 30);

      console.debug(`Final position: section=${sectionIndex}, pos=${positionPercentage.toFixed(2)}%, frame=${newFrame}`);

      // Update player and both section indices
      if (playerRef.current) {
        playerRef.current.seekTo(newFrame);
        setCurrentFrame(newFrame);
        setGlobalCurrentFrame(newFrame);
        safeSetActiveSectionIndex(sectionIndex);
        setSelectedSectionIndex(sectionIndex);
      }
    }

    // Clear locked section
    setLockedSectionIndex(null);

    // End dragging state
    setIsDraggingPlayhead(false);

    setTimeout(() => {
      if (!isPlayheadHovered) {
        setIsPlayheadHovered(false);
      }
    }, 100);

    if ('preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Add global mouse event handlers
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent | TouchEvent) => {
      if (isDraggingPlayhead) {
        handlePlayheadMouseMove(e as MouseEvent);
      }
    };

    const handleGlobalMouseUp = (e: MouseEvent) => {
      if (isDraggingPlayhead) {
        handlePlayheadMouseUp(e as MouseEvent);
      }
    };

    if (isDraggingPlayhead) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingPlayhead, dragStartX, activeSectionIndex, lockedSectionIndex]);

  // Add effect to continuously update playhead position
  useEffect(() => {
    if (!playheadRef.current) return;

    // Use requestAnimationFrame for smoother updates
    const updatePlayhead = () => {
      // We're now calculating position directly in the render function
      // so this is no longer needed
    };

    // Initial update
    updatePlayhead();

    // No need for interval anymore
    const intervalId = setInterval(updatePlayhead, 33); // ~30fps

    return () => clearInterval(intervalId);
  }, [currentFrame, activeSectionIndex]);

  // Playhead hover handlers
  const handlePlayheadMouseEnter = () => {
    setIsPlayheadHovered(true);
  };

  const handlePlayheadMouseLeave = () => {
    if (!isDraggingPlayhead) {
      setIsPlayheadHovered(false);
    }
  };

  // Add effect to handle section changes based on current frame
  useEffect(() => {
    if (!jsonPages?.pages || currentFrame === undefined) return;

    // Skip checking for section changes when actively dragging the playhead
    if (isDraggingPlayhead) return;

    const currentTimeMs = (currentFrame / 30) * 1000;
    let accumulatedTime = 0;
    let newActiveSection = activeSectionIndex;

    // Group pages by section
    const sections = groupPagesByScriptIndex(jsonPages.pages);

    // Find the correct section based on current time
    for (let i = 0; i < Object.keys(sections).length; i++) {
      const sectionPages = sections[i] || [];
      const sectionDuration = sectionPages.reduce((sum: number, page: any) => sum + page.duration, 0);

      if (currentTimeMs >= accumulatedTime && currentTimeMs < accumulatedTime + sectionDuration) {
        newActiveSection = i;
        break;
      }

      accumulatedTime += sectionDuration;
    }

    // Update active section if changed
    if (newActiveSection !== activeSectionIndex) {

      safeSetActiveSectionIndex(newActiveSection);
    }
  }, [currentFrame, jsonPages, activeSectionIndex, isDraggingPlayhead]);

  // Add these handler functions for caption dragging after handleCaptionCancel
  const handleCaptionDragStart = (
    word: Word,
    index: number,
    sectionIndex: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setCaptionDragState({
      word,
      index,
      sectionIndex,
      initialStart: word.start,
      initialEnd: word.end,
      isDragging: true
    });
    try {
      document.body.setAttribute('data-dragging', 'true');
    } catch (err) {
      console.error('Error setting dragging attribute:', err);
    }
  };

  const handleCaptionDrag = (
    e: MouseEvent | TouchEvent,
    sectionDurationSecs: number,
    sectionStartTime: number
  ) => {
    if (!captionDragState || !timelineRef.current) return;

    // Get the section element
    const sectionElement = document.querySelector(`[data-section-index="${captionDragState.sectionIndex}"]`);
    if (!sectionElement) return;

    const sectionRect = sectionElement.getBoundingClientRect();
    const captionTrack = sectionElement.querySelector('.captions-track');
    if (!captionTrack) return;

    const trackRect = captionTrack.getBoundingClientRect();

    // Calculate mouse position relative to the section
    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    const relativeX = clientX - trackRect.left;
    const percentX = Math.max(0, Math.min(100, (relativeX / trackRect.width) * 100));

    // Calculate the new time based on percentage
    const wordDuration = captionDragState.initialEnd - captionDragState.initialStart;
    const newStartTime = (percentX / 100) * sectionDurationSecs;

    // Ensure caption stays within section bounds
    const clampedStartTime = Math.max(0, Math.min(sectionDurationSecs - wordDuration, newStartTime));
    const newEndTime = clampedStartTime + wordDuration;

    // Update script details with new timing
    updateCaptionTiming(
      captionDragState.sectionIndex,
      captionDragState.index,
      clampedStartTime,
      newEndTime
    );
  };

  const handleCaptionResizeStart = (
    edge: 'left' | 'right',
    word: Word,
    index: number,
    sectionIndex: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setCaptionDragState({
      word,
      index,
      sectionIndex,
      initialStart: word.start,
      initialEnd: word.end,
      isDragging: true
    });
    setIsResizingCaption(true);
    try {
      document.body.setAttribute('data-dragging', 'true');
    } catch (err) {
      console.error('Error setting dragging attribute:', err);
    }
  };

  const handleCaptionResize = (
    edge: 'left' | 'right',
    e: MouseEvent | TouchEvent,
    sectionDurationSecs: number
  ) => {
    if (!captionDragState || !timelineRef.current) return;

    // Get the section element
    const sectionElement = document.querySelector(`[data-section-index="${captionDragState.sectionIndex}"]`);
    if (!sectionElement) return;

    const captionTrack = sectionElement.querySelector('.captions-track');
    if (!captionTrack) return;

    const trackRect = captionTrack.getBoundingClientRect();

    // Calculate mouse position relative to the track
    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    const relativeX = clientX - trackRect.left;
    const percentX = Math.max(0, Math.min(100, (relativeX / trackRect.width) * 100));

    // Calculate the new time based on percentage
    const newTime = (percentX / 100) * sectionDurationSecs;

    // Minimum caption duration in seconds (e.g., 0.1 seconds)
    const MIN_DURATION = 0.1;

    let newStart = captionDragState.word.start;
    let newEnd = captionDragState.word.end;

    if (edge === 'left') {
      // Update start time, ensuring it doesn't go beyond end - MIN_DURATION
      newStart = Math.min(newTime, captionDragState.word.end - MIN_DURATION);
      newStart = Math.max(0, newStart); // Ensure it doesn't go below 0
    } else {
      // Update end time, ensuring it doesn't go below start + MIN_DURATION
      newEnd = Math.max(newTime, captionDragState.word.start + MIN_DURATION);
      newEnd = Math.min(sectionDurationSecs, newEnd); // Ensure it doesn't exceed section duration
    }

    // Update script details with new timing
    updateCaptionTiming(
      captionDragState.sectionIndex,
      captionDragState.index,
      newStart,
      newEnd
    );
  };

  const handleCaptionDragStop = () => {
    if (!captionDragState) return;

    setCaptionDragState(null);
    setIsResizingCaption(false);
    try {
      document.body.removeAttribute('data-dragging');
    } catch (err) {
      console.error('Error removing dragging attribute:', err);
    }
  };

  const updateCaptionTiming = (
    sectionIndex: number,
    wordIndex: number,
    newStart: number,
    newEnd: number
  ) => {
    if (!scriptDetails?.data[sectionIndex]?.captions?.[0]?.words) return;

    // Create a copy of the words array
    const updatedWords = [...scriptDetails.data[sectionIndex].captions[0].words];

    // Update the specific word with new timing
    updatedWords[wordIndex] = {
      ...updatedWords[wordIndex],
      start: newStart,
      end: newEnd
    };

    // Update the script details
    setScriptDetails((prevScript: any) => {
      const updatedScript = { ...prevScript };
      const updatedData = [...prevScript.data];
      updatedData[sectionIndex] = {
        ...updatedData[sectionIndex],
        captions: [{ ...updatedData[sectionIndex].captions[0], words: updatedWords }]
      };
      return { ...updatedScript, data: updatedData };
    });
  };
  // Add these event listeners for global mouse events related to caption dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent | TouchEvent) => {
      if (captionDragState && captionDragState.isDragging) {
        if (jsonPages?.pages) {
          const { startTime, endTime } = calculateSectionDuration(captionDragState.sectionIndex);
          const sectionDurationMs = endTime - startTime;
          const sectionDurationSecs = sectionDurationMs / 1000;

          if (isResizingCaption) {
            // Determine which edge is being resized based on mouse position relative to caption center
            const captionRef = captionRefs.current[`${captionDragState.sectionIndex}-${captionDragState.index}`];
            if (!captionRef) return;

            const captionRect = captionRef.getBoundingClientRect();
            const captionCenter = captionRect.left + captionRect.width / 2;

            // Handle both mouse and touch events
            const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            const edge = clientX < captionCenter ? 'left' : 'right';

            handleCaptionResize(edge, e, sectionDurationSecs);
          } else {
            handleCaptionDrag(e, sectionDurationSecs, startTime / 1000);
          }
        }
      }
    };

    const handleGlobalMouseUp = () => {
      if (captionDragState && captionDragState.isDragging) {
        handleCaptionDragStop();
      }
    };

    if (captionDragState && captionDragState.isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove as EventListener);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalMouseMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove as EventListener);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalMouseMove as EventListener);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [captionDragState, isResizingCaption, jsonPages]);

  // Add keyboard event listeners to detect Ctrl/Cmd key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Add this function to handle section scroll
  const handleSectionScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Prevent parent scrolling when section content is scrolled
    e.stopPropagation();
  };

  // Add this new function to calculate section positions
  const calculateSectionPosition = (sectionIndex: number) => {
    let position = 0;
    for (let i = 0; i < sectionIndex; i++) {
      const sectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
      position += sectionPages.reduce((sum: number, page: any) => sum + page.duration, 0);
    }
    return position;
  };

  // Add state for tracking mouse position during cross-section drag
  const [globalMousePos, setGlobalMousePos] = useState({ x: 0, y: 0 });
  const timelineContentRef = useRef<HTMLDivElement>(null);

  // Add function to find section at mouse position
  const findSectionAtPosition = useCallback((clientX: number) => {
    if (!timelineContentRef.current) return null;

    const sections = timelineContentRef.current.querySelectorAll('.timeline-section');
    for (let i = 0; i < sections.length; i++) {
      const rect = sections[i].getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return {
          index: parseInt(sections[i].getAttribute('data-section-index') || '0'),
          rect: rect
        };
      }
    }
    return null;
  }, []);

  // Add debounced update function
  const debouncedUpdateSfx = useCallback(
    debounce((updatedSfx: any) => {
      setScriptDetails((prevScript: any) => ({
        ...prevScript,
        sfxs: updatedSfx
      }));
    }, 16), // 60fps
    []
  );

  // Add this helper function to find global SFX index
  const findGlobalSfxIndex = (sfx: SFX) => {
    return scriptDetails.sfxs.findIndex((s: SFX) =>
      s.start === sfx.start &&
      s.end === sfx.end &&
      s.name === sfx.name &&
      s.section === sfx.section
    );
  };

  const handleSfxDragStart = (
    sfx: SFX,
    index: number,
    sectionIndex: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    // Find the global index in scriptDetails.sfxs
    const globalIndex = findGlobalSfxIndex(sfx);
    if (globalIndex === -1) {
      console.error('Could not find SFX in global array');
      return;
    }

    setSfxDragState({
      sfx,
      index: globalIndex, // Use global index instead of section-specific index
      sectionIndex,
      initialStart: sfx.start,
      initialEnd: sfx.end,
      isDragging: true
    });


    try {
      document.body.setAttribute('data-dragging', 'true');
    } catch (err) {
      console.error('Error setting dragging attribute:', err);
    }
  };

  const handleSfxDrag = (
    e: MouseEvent | TouchEvent,
    sectionDurationSecs: number,
    sectionStartTime: number
  ) => {
    if (!sfxDragState || !timelineContentRef.current) return;

    const now = performance.now();
    if (now - lastDragUpdateRef.current < DRAG_UPDATE_INTERVAL) {
      return;
    }
    lastDragUpdateRef.current = now;

    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    // Find which section we're currently over
    const targetSection = findSectionAtPosition(clientX);
    if (!targetSection) return;

    // Remove drag-over class from all tracks
    document.querySelectorAll('.sfx-track').forEach(track => {
      track.classList.remove('drag-over');
    });

    // Add drag-over class to current track
    const currentTrack = document.querySelector(`[data-section-index="${targetSection.index}"] .sfx-track`);
    if (currentTrack) {
      currentTrack.classList.add('drag-over');
    }

    const sfxTrack = document.querySelector(`[data-section-index="${targetSection.index}"] .sfx-track`);
    if (!sfxTrack) return;

    const trackRect = sfxTrack.getBoundingClientRect();
    const relativeX = clientX - trackRect.left;
    const percentX = Math.max(0, Math.min(100, (relativeX / trackRect.width) * 100));

    // Calculate cumulative time at the start of the target section
    let targetSectionStartTime = 0;
    for (let i = 0; i < targetSection.index; i++) {
      const prevSectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
      targetSectionStartTime += prevSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;
    }

    // Calculate target section duration
    const targetSectionPages = groupPagesByScriptIndex(jsonPages.pages)[targetSection.index] || [];
    const targetSectionDuration = targetSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;

    // Calculate new times in the target section
    const sfxDuration = sfxDragState.sfx.end - sfxDragState.sfx.start;
    const newStartTime = targetSectionStartTime + ((percentX / 100) * targetSectionDuration);
    const newEndTime = newStartTime + sfxDuration;

    // Update the correct SFX using the global index
    const updatedSfxs = scriptDetails.sfxs.map((sfx: SFX, idx: number) => {
      if (idx === sfxDragState.index) {
        return {
          ...sfx,
          start: newStartTime,
          end: newEndTime,
          section: targetSection.index
        };
      }
      return sfx;
    });

    // Use requestAnimationFrame for smooth updates
    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
    }

    dragAnimationFrameRef.current = requestAnimationFrame(() => {
      setScriptDetails((prevScript: any) => ({
        ...prevScript,
        sfxs: updatedSfxs
      }));
    });
  };

  // Add SFX resize handlers
  const handleSfxResizeStart = (
    edge: 'left' | 'right',
    sfx: SFX,
    index: number,
    sectionIndex: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setSfxDragState({
      sfx,
      index,
      sectionIndex,
      initialStart: sfx.start,
      initialEnd: sfx.end,
      isDragging: true
    });
    setIsResizingSfx(true);
    try {
      document.body.setAttribute('data-dragging', 'true');
    } catch (err) {
      console.error('Error setting dragging attribute:', err);
    }
  };

  // Update handleSfxResize to work with cumulative times
  const handleSfxResize = (
    edge: 'left' | 'right',
    e: MouseEvent | TouchEvent,
    sectionDurationSecs: number
  ) => {
    if (!sfxDragState || !timelineRef.current) return;

    const sectionElement = document.querySelector(`[data-section-index="${sfxDragState.sectionIndex}"]`);
    if (!sectionElement) return;

    const sfxTrack = sectionElement.querySelector('.sfx-track');
    if (!sfxTrack) return;

    const trackRect = sfxTrack.getBoundingClientRect();

    let clientX: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }

    const relativeX = clientX - trackRect.left;
    const percentX = Math.max(0, Math.min(100, (relativeX / trackRect.width) * 100));

    // Calculate section start time (cumulative)
    let sectionStartTime = 0;
    for (let i = 0; i < sfxDragState.sectionIndex; i++) {
      const prevSectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
      sectionStartTime += prevSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;
    }

    // Calculate new time in the section
    const newTimeInSection = (percentX / 100) * sectionDurationSecs;
    const newAbsoluteTime = sectionStartTime + newTimeInSection;

    // Minimum SFX duration in seconds
    const MIN_DURATION = 0.1;

    let newStart = sfxDragState.sfx.start;
    let newEnd = sfxDragState.sfx.end;

    if (edge === 'left') {
      // Update start time, ensuring it doesn't go beyond end - MIN_DURATION
      newStart = Math.min(newAbsoluteTime, sfxDragState.sfx.end - MIN_DURATION);
      newStart = Math.max(sectionStartTime, newStart);
    } else {
      // Update end time, ensuring it doesn't go below start + MIN_DURATION
      newEnd = Math.max(newAbsoluteTime, sfxDragState.sfx.start + MIN_DURATION);
      newEnd = Math.min(sectionStartTime + sectionDurationSecs, newEnd);
    }

    // Update script details with new timing
    setScriptDetails((prevScript: any) => {
      const updatedScript = { ...prevScript };
      const updatedSfx = [...prevScript.sfxs];
      updatedSfx[sfxDragState.index] = {
        ...updatedSfx[sfxDragState.index],
        start: newStart,
        end: newEnd
      };
      return { ...updatedScript, sfxs: updatedSfx };
    });
  };

  // Update the effect for SFX dragging to handle resize
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent | TouchEvent) => {
      if (sfxDragState && sfxDragState.isDragging) {
        if (jsonPages?.pages) {
          const { startTime, endTime } = calculateSectionDuration(sfxDragState.sectionIndex);
          const sectionDurationMs = endTime - startTime;
          const sectionDurationSecs = sectionDurationMs / 1000;

          if (isResizingSfx) {
            const sfxRef = sfxRefs.current[`${sfxDragState.sectionIndex}-${sfxDragState.index}`];
            if (!sfxRef) return;

            const sfxRect = sfxRef.getBoundingClientRect();
            const sfxCenter = sfxRect.left + sfxRect.width / 2;

            const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            const edge = clientX < sfxCenter ? 'left' : 'right';

            handleSfxResize(edge, e, sectionDurationSecs);
          } else {
            handleSfxDrag(e, sectionDurationSecs, startTime / 1000);
          }
        }
      }
    };

    const handleGlobalMouseUp = () => {
      if (sfxDragState && sfxDragState.isDragging) {
        handleSfxDragStop();
      }
    };

    if (sfxDragState && sfxDragState.isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove as EventListener);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalMouseMove as EventListener);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove as EventListener);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalMouseMove as EventListener);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [sfxDragState, isResizingSfx, jsonPages]);

  const handleSfxDragStop = () => {
    if (!sfxDragState) return;

    // Remove drag-over class from all tracks
    document.querySelectorAll('.sfx-track').forEach(track => {
      track.classList.remove('drag-over');
    });

    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
    }

    setIsDraggingSfx(false);
    setSfxDragState(null);
    setIsResizingSfx(false);
    try {
      document.body.removeAttribute('data-dragging');
    } catch (err) {
      console.error('Error removing dragging attribute:', err);
    }
  };

  const handleSfxDuplicate = (sfx: SFX) => {
    setScriptDetails((prevScript: any) => {
      if (!prevScript.sfxs) return prevScript;
      // Find the SFX by id
      const targetSfx = prevScript.sfxs.find((s: SFX) => s.id === sfx.id);
      if (!targetSfx) {
        console.error(`SFX not found by id ${sfx.id}`);
        return prevScript;
      }
      // Create the duplicate with offset and new UUID
      const newSfx = {
        ...targetSfx,
        start: targetSfx.start + 0.5, // Offset slightly to make it visible
        end: targetSfx.end + 0.5,
        id: uuidv4(),
      };
      return {
        ...prevScript,
        sfxs: [...prevScript.sfxs, newSfx]
      };
    });
    message.success('SFX duplicated successfully');
  };

  const handleSfxMuteToggle = (sfxId: string) => {
    setScriptDetails((prevScript: any) => {
      if (!prevScript.sfxs) return prevScript;

      // Find the SFX by id
      const globalIndex = prevScript.sfxs.findIndex((sfx: SFX) => sfx.id === sfxId);
      if (globalIndex === -1) {
        console.error('SFX not found by id');
        return prevScript;
      }

      // Create new array with the updated SFX
      const updatedSfxs = [...prevScript.sfxs];
      updatedSfxs[globalIndex] = {
        ...updatedSfxs[globalIndex],
        volume: updatedSfxs[globalIndex].volume === 0 ? 0.8 : 0 // Toggle between muted (0) and default volume (0.8)
      };
      console.log(updatedSfxs)
      return {
        ...prevScript,
        sfxs: updatedSfxs
      };
    });
  };

  const handleSfxDelete = (sfxId: string) => {
    setScriptDetails((prevScript: any) => {
      if (!prevScript.sfxs) return prevScript;

      // Find the SFX by id
      const globalIndex = prevScript.sfxs.findIndex((sfx: SFX) => sfx.id === sfxId);
      if (globalIndex === -1) {
        console.error('SFX not found by id');
        return prevScript;
      }

      // Create a copy of the sfxs array and remove the target SFX
      const updatedSfxs = [...prevScript.sfxs];
      updatedSfxs.splice(globalIndex, 1);

      return {
        ...prevScript,
        sfxs: updatedSfxs
      };
    });

    message.success('SFX deleted successfully');
  };
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(scriptDetails?.sfxs)
  }, [scriptDetails?.sfxs])

  // Add this near other state declarations
  const [isDraggingSfx, setIsDraggingSfx] = useState(false);
  const dragAnimationFrameRef = useRef<number>();
  const lastDragUpdateRef = useRef<number>(0);
  const DRAG_UPDATE_INTERVAL = 1000 / 60; // 60fps

  // Guard: scriptDetails (and its sfxs array) may be undefined during the
  // initial mount that now happens before data loads. Return null until ready.
  if (!scriptDetails?.data) return null;

  return (
    <div className="timeline-container">
      <div className="timeline-header" style={{
        backgroundColor: 'var(--primary-color-bg, rgba(24, 144, 255, 0.05))',
        borderBottom: `1px solid ${getPrimaryColorLight()}`
      }}>
        <div className="timeline-controls">
          <PlayPauseButton playerRef={playerRef} />
          <div className="time-display" style={{ color: getPrimaryColorDark(), fontWeight: 500 }}>
            {formatTime((currentFrame / 30) * 1000)} / {formatTime(totalDuration)}
          </div>
        </div>
      </div>

      <div
        className="timeline-content"
        ref={timelineContentRef}
        style={{
          position: 'relative',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div
          className="timeline-sections"
          ref={timelineRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            minWidth: '100%',
            position: 'relative'
          }}
        >
          {jsonPages?.pages && Object.entries(groupPagesByScriptIndex(jsonPages.pages)).map(([scriptIndex, pages]: [string, any]) => {
            const sectionWords = scriptDetails?.data[parseInt(scriptIndex)]?.captions?.[0]?.words || [];
            const totalSectionDuration = pages.reduce((total: number, page: any) => total + page.duration, 0);
            const wordsWithinDuration = getWordsWithinDuration(sectionWords, totalSectionDuration);
            const { startTime, endTime } = calculateSectionDuration(parseInt(scriptIndex));
            const sectionDuration = endTime - startTime;
            const isActiveSection = parseInt(scriptIndex) === activeSectionIndex;
            const sectionPosition = calculateSectionPosition(parseInt(scriptIndex));

            // Calculate section-specific playhead position
            let sectionPlayheadPosition = 0;
            if (isActiveSection) {
              const currentTimeMs = (currentFrame / 30) * 1000;
              const positionInSection = currentTimeMs - startTime;

              if (positionInSection >= 0) {
                if (positionInSection >= sectionDuration) {
                  sectionPlayheadPosition = 100;
                } else {
                  sectionPlayheadPosition = (positionInSection / sectionDuration) * 100;
                }
              }
            } else if (parseInt(scriptIndex) < activeSectionIndex) {
              sectionPlayheadPosition = 100;
            }

            const sectionSfx = scriptDetails?.sfxs?.filter((sfx: SFX) => {
              // Calculate cumulative time for this section's start and end
              let sectionStartTime = 0;
              let sectionEndTime = 0;

              // Sum up all previous sections' durations to get start time
              for (let i = 0; i < parseInt(scriptIndex); i++) {
                const prevSectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
                sectionStartTime += prevSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;
              }

              // Add current section duration to get end time
              const currentSectionPages = pages;
              sectionEndTime = sectionStartTime + currentSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;

              // Check if this SFX belongs in this section based on its start time
              return sfx.start >= sectionStartTime && sfx.start < sectionEndTime;
            }) || [];

            const currentTimeMs = (currentFrame / 30) * 1000;
            return (
              <div
                key={`section-${scriptIndex}`}
                className={`timeline-section ${isActiveSection ? 'active-section' : ''}`}
                data-section-index={scriptIndex}
                data-active={isActiveSection ? 'true' : undefined}
                style={{
                  position: 'relative',
                  minWidth: `${(totalSectionDuration / totalDuration) * 100}%`,
                  flexShrink: 0,
                  backgroundColor: isActiveSection ? 'var(--color-primary-50)' : 'transparent',
                  borderColor: isActiveSection ? 'var(--color-primary-200)' : 'transparent',
                  transition: 'background-color 0.3s ease, border-color 0.3s ease'
                }}
              >
                <div className="section-header">
                  <div className="section-title">
                    Section {parseInt(scriptIndex) + 1}
                  </div>
                  <div className="section-duration">
                    {`${(startTime / 1000).toFixed(1)}s`} - {`${(endTime / 1000).toFixed(1)}s`}
                  </div>
                </div>

                <div
                  className="section-content"
                  onClick={(e) => handleTimelineClick(e, parseInt(scriptIndex))}
                  onScroll={handleSectionScroll}
                  style={{ cursor: 'pointer' }}
                >
                  {renderTimeScale(startTime, endTime, isActiveSection)}

                  <div className="scenes-track">
                    {pages.map((page: any, pageIndex: number) => (
                      <div
                        key={page.id}
                        className="scene-block"
                        data-page-index={pageIndex}
                        style={{
                          width: `${(page.duration / totalSectionDuration) * 100}%`,
                          backgroundColor: isActiveSection ? 'var(--primary-color-bg, rgba(24, 144, 255, 0.05))' : 'rgba(0, 0, 0, 0.02)'
                        }}
                      >
                        <div className="scene-preview">
                          {imagePreviews[page.id] && (
                            [...Array(Math.ceil(page.duration / 100))].map((_, i) => (
                              <img
                                key={i}
                                src={imagePreviews[page.id]}
                                alt={`Scene ${page.id}`}
                                style={{
                                  width: `${100 / Math.ceil(page.duration / 100)}%`,
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'inline-block'
                                }}
                              />
                            ))
                          )}
                        </div>

                        {/* Left resize handle */}
                        <div
                          className="resize-handle resize-handle-left"
                          onMouseDown={(e) => {
                            e.stopPropagation();

                            // Store initial values
                            const startX = e.clientX;
                            const startWidth = page.duration;
                            const sceneBlock = e.currentTarget.parentElement;
                            const track = sceneBlock?.parentElement;

                            if (!track || !sceneBlock) return;

                            const trackWidth = track.getBoundingClientRect().width;

                            // Check scene position
                            const sectionPages = pages.filter((p: any) => p?.custom?.scriptIndex === parseInt(scriptIndex));
                            const isSingleScene = sectionPages.length === 1;
                            const isFirstScene = pageIndex === 0;

                            // Find previous page info if not the first scene
                            let prevPageId: string | null = null;
                            let prevPageElement: HTMLElement | null = null;
                            let prevPageInitialWidth = 0;

                            if (!isFirstScene && !isSingleScene && pageIndex > 0) {
                              try {
                                // Get the previous page
                                const prevPageData = pages[pageIndex - 1];
                                if (prevPageData) {
                                  prevPageId = prevPageData.id;
                                  prevPageInitialWidth = prevPageData.duration;

                                  // Find the previous page element
                                  const allSceneBlocks = track.querySelectorAll('.scene-block');
                                  for (let i = 0; i < allSceneBlocks.length; i++) {
                                    const block = allSceneBlocks[i] as HTMLElement;
                                    if (block.dataset.pageIndex === (pageIndex - 1).toString()) {
                                      prevPageElement = block;
                                      break;
                                    }
                                  }
                                }
                              } catch (err) {
                                console.error('Error finding previous page:', err);
                              }
                            }

                            // Create a temporary drag indicator for first scene
                            let dragIndicator: HTMLElement | null = null;
                            if (isFirstScene) {
                              try {
                                dragIndicator = document.createElement('div');
                                dragIndicator.className = 'drag-extension-indicator';
                                dragIndicator.style.position = 'absolute';
                                dragIndicator.style.top = '0';
                                dragIndicator.style.left = '0';
                                dragIndicator.style.height = '100%';
                                dragIndicator.style.width = '0';
                                dragIndicator.style.backgroundColor = 'var(--color-primary-300)';
                                dragIndicator.style.opacity = '0.7';
                                dragIndicator.style.pointerEvents = 'none';
                                dragIndicator.style.transition = 'none';
                                dragIndicator.style.zIndex = '5';
                                sceneBlock.appendChild(dragIndicator);
                              } catch (err) {
                                console.error('Error creating drag indicator:', err);
                              }
                            }

                            // Mark dragging state
                            try {
                              document.body.setAttribute('data-dragging', 'true');
                            } catch (err) {
                              // Ignore errors
                            }

                            // Style the handle
                            try {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
                            } catch (err) {
                              // Ignore errors
                            }

                            const handleMouseMove = (moveEvent: MouseEvent) => {
                              moveEvent.preventDefault();

                              const deltaX = moveEvent.clientX - startX;
                              const deltaPercentage = (deltaX / trackWidth) * 100;

                              // For left resize, negative deltaX means increasing width
                              const newDurationPercentage = Math.max(
                                1, // Minimum 1% width
                                (startWidth / totalSectionDuration * 100) - deltaPercentage
                              );

                              try {
                                // Show indicator for first scene
                                if (isFirstScene && deltaX < 0 && dragIndicator) {
                                  const indicatorWidth = `${-deltaPercentage}%`;
                                  dragIndicator.style.width = indicatorWidth;
                                }

                                if (sceneBlock) {
                                  // For single scenes, maintain 100% width
                                  if (isSingleScene) {
                                    sceneBlock.style.width = '100%';
                                  } else {
                                    sceneBlock.style.width = `${newDurationPercentage}%`;

                                    // Adjust previous scene width
                                    if (!isFirstScene && prevPageElement && deltaX !== 0) {
                                      const prevPercentage = (prevPageInitialWidth / totalSectionDuration * 100) + deltaPercentage;
                                      const safePrevPercentage = Math.max(1, prevPercentage);
                                      prevPageElement.style.width = `${safePrevPercentage}%`;
                                    }
                                  }
                                }
                              } catch (err) {
                                console.error('Error during mouse move:', err);
                              }
                            };

                            const handleMouseUp = (upEvent: MouseEvent) => {
                              // Clean up
                              try {
                                document.body.removeAttribute('data-dragging');
                              } catch (err) {
                                // Ignore errors
                              }

                              // Remove indicator
                              if (dragIndicator && sceneBlock) {
                                try {
                                  sceneBlock.removeChild(dragIndicator);
                                } catch (err) {
                                  // Ignore errors
                                }
                              }

                              // Calculate changes
                              const deltaX = upEvent.clientX - startX;
                              const deltaPercentage = (deltaX / trackWidth) * 100;
                              const durationChangeMs = (deltaPercentage / 100) * totalSectionDuration;

                              // Update durations
                              try {
                                if (isFirstScene || isSingleScene) {
                                  // For first/single scene, adjust only this page
                                  const newDuration = Math.max(500, startWidth - durationChangeMs);

                                  const pageToUpdate = store.pages.find((p: any) => p.id === page.id);
                                  if (pageToUpdate) {
                                    pageToUpdate.set({ duration: newDuration });

                                    // Keep single scenes at 100% width
                                    if (isSingleScene && sceneBlock) {
                                      sceneBlock.style.width = '100%';
                                    }
                                  }
                                } else {
                                  // For non-first scenes, adjust this and previous page
                                  const currentPageNewDuration = Math.max(500, startWidth - durationChangeMs);

                                  // Update current page
                                  const pageToUpdate = store.pages.find((p: any) => p.id === page.id);
                                  if (pageToUpdate) {
                                    pageToUpdate.set({ duration: currentPageNewDuration });
                                  }

                                  // Update previous page if available
                                  if (prevPageId) {
                                    const prevPageToUpdate = store.pages.find((p: any) => p.id === prevPageId);
                                    if (prevPageToUpdate) {
                                      const prevPageNewDuration = Math.max(500, prevPageInitialWidth + durationChangeMs);
                                      prevPageToUpdate.set({ duration: prevPageNewDuration });
                                    }
                                  }
                                }

                                // Update store
                                setJsonPages(store.toJSON());
                              } catch (err) {
                                console.error('Error updating page durations:', err);
                              }

                              // Remove listeners
                              document.removeEventListener('mousemove', handleMouseMove);
                              document.removeEventListener('mouseup', handleMouseUp);
                            };

                            document.addEventListener('mousemove', handleMouseMove);
                            document.addEventListener('mouseup', handleMouseUp);
                          }}
                        >
                          <div className="handle-icon"></div>
                        </div>

                        {/* Right resize handle */}
                        <div
                          className="resize-handle resize-handle-right"
                          onMouseDown={(e) => {
                            e.stopPropagation();

                            // Store initial values
                            const startX = e.clientX;
                            const startWidth = page.duration;
                            const sceneBlock = e.currentTarget.parentElement;
                            const track = sceneBlock?.parentElement;

                            if (!track || !sceneBlock) return;

                            const trackWidth = track.getBoundingClientRect().width;

                            // Check scene position
                            const sectionPages = pages.filter((p: any) => p?.custom?.scriptIndex === parseInt(scriptIndex));
                            const isSingleScene = sectionPages.length === 1;
                            const isLastScene = pageIndex === pages.length - 1;

                            // Find next page info if not the last scene
                            let nextPageId: string | null = null;
                            let nextPageElement: HTMLElement | null = null;
                            let nextPageInitialWidth = 0;

                            if (!isLastScene && !isSingleScene && pageIndex + 1 < pages.length) {
                              try {
                                // Get the next page
                                const nextPageData = pages[pageIndex + 1];
                                if (nextPageData) {
                                  nextPageId = nextPageData.id;
                                  nextPageInitialWidth = nextPageData.duration;

                                  // Find the next page element
                                  const allSceneBlocks = track.querySelectorAll('.scene-block');
                                  for (let i = 0; i < allSceneBlocks.length; i++) {
                                    const block = allSceneBlocks[i] as HTMLElement;
                                    if (block.dataset.pageIndex === (pageIndex + 1).toString()) {
                                      nextPageElement = block;
                                      break;
                                    }
                                  }
                                }
                              } catch (err) {
                                console.error('Error finding next page:', err);
                              }
                            }

                            // Create a temporary drag indicator for last scene
                            let dragIndicator: HTMLElement | null = null;
                            if (isLastScene) {
                              try {
                                dragIndicator = document.createElement('div');
                                dragIndicator.className = 'drag-extension-indicator';
                                dragIndicator.style.position = 'absolute';
                                dragIndicator.style.top = '0';
                                dragIndicator.style.right = '0';
                                dragIndicator.style.height = '100%';
                                dragIndicator.style.width = '0';
                                dragIndicator.style.backgroundColor = 'var(--color-primary-300)';
                                dragIndicator.style.opacity = '0.7';
                                dragIndicator.style.pointerEvents = 'none';
                                dragIndicator.style.transition = 'none';
                                dragIndicator.style.zIndex = '5';
                                sceneBlock.appendChild(dragIndicator);
                              } catch (err) {
                                console.error('Error creating drag indicator:', err);
                              }
                            }

                            // Mark dragging state
                            try {
                              document.body.setAttribute('data-dragging', 'true');
                            } catch (err) {
                              // Ignore errors
                            }

                            // Style the handle
                            try {
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
                            } catch (err) {
                              // Ignore errors
                            }

                            const handleMouseMove = (moveEvent: MouseEvent) => {
                              moveEvent.preventDefault();

                              const deltaX = moveEvent.clientX - startX;
                              const deltaPercentage = (deltaX / trackWidth) * 100;

                              // Calculate new duration percentage
                              const newDurationPercentage = Math.max(
                                1, // Minimum 1% width
                                (startWidth / totalSectionDuration * 100) + deltaPercentage
                              );

                              try {
                                // Show indicator for last scene
                                if (isLastScene && deltaX > 0 && dragIndicator) {
                                  const indicatorWidth = `${deltaPercentage}%`;
                                  dragIndicator.style.width = indicatorWidth;
                                }

                                if (sceneBlock) {
                                  // For single scenes, maintain 100% width
                                  if (isSingleScene) {
                                    sceneBlock.style.width = '100%';
                                  } else {
                                    sceneBlock.style.width = `${newDurationPercentage}%`;

                                    // Adjust next scene width
                                    if (!isLastScene && nextPageElement && deltaX !== 0) {
                                      const nextPercentage = (nextPageInitialWidth / totalSectionDuration * 100) - deltaPercentage;
                                      const safeNextPercentage = Math.max(1, nextPercentage);
                                      nextPageElement.style.width = `${safeNextPercentage}%`;
                                    }
                                  }
                                }
                              } catch (err) {
                                console.error('Error during mouse move:', err);
                              }
                            };

                            const handleMouseUp = (upEvent: MouseEvent) => {
                              // Clean up
                              try {
                                document.body.removeAttribute('data-dragging');
                              } catch (err) {
                                // Ignore errors
                              }

                              // Remove indicator
                              if (dragIndicator && sceneBlock) {
                                try {
                                  sceneBlock.removeChild(dragIndicator);
                                } catch (err) {
                                  // Ignore errors
                                }
                              }

                              // Calculate changes
                              const deltaX = upEvent.clientX - startX;
                              const deltaPercentage = (deltaX / trackWidth) * 100;
                              const durationChangeMs = (deltaPercentage / 100) * totalSectionDuration;

                              // Update durations
                              try {
                                if (isLastScene || isSingleScene) {
                                  // For last/single scene, adjust only this page
                                  const newDuration = Math.max(500, startWidth + durationChangeMs);

                                  const pageToUpdate = store.pages.find((p: any) => p.id === page.id);
                                  if (pageToUpdate) {
                                    pageToUpdate.set({ duration: newDuration });

                                    // Keep single scenes at 100% width
                                    if (isSingleScene && sceneBlock) {
                                      sceneBlock.style.width = '100%';
                                    }
                                  }
                                } else {
                                  // For non-last scenes, adjust this and next page
                                  const currentPageNewDuration = Math.max(500, startWidth + durationChangeMs);

                                  // Update current page
                                  const pageToUpdate = store.pages.find((p: any) => p.id === page.id);
                                  if (pageToUpdate) {
                                    pageToUpdate.set({ duration: currentPageNewDuration });
                                  }

                                  // Update next page if available
                                  if (nextPageId) {
                                    const nextPageToUpdate = store.pages.find((p: any) => p.id === nextPageId);
                                    if (nextPageToUpdate) {
                                      const nextPageNewDuration = Math.max(500, nextPageInitialWidth - durationChangeMs);
                                      nextPageToUpdate.set({ duration: nextPageNewDuration });
                                    }
                                  }
                                }

                                // Update store
                                setJsonPages(store.toJSON());
                              } catch (err) {
                                console.error('Error updating page durations:', err);
                              }

                              // Remove listeners
                              document.removeEventListener('mousemove', handleMouseMove);
                              document.removeEventListener('mouseup', handleMouseUp);
                            };

                            document.addEventListener('mousemove', handleMouseMove);
                            document.addEventListener('mouseup', handleMouseUp);
                          }}
                        >
                          <div className="handle-icon"></div>
                        </div>

                        <div className="scene-duration">
                          {formatTime(page.duration)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {isActiveSection && (
                    <div
                      className="section-playhead"
                      ref={isActiveSection ? playheadRef : null}
                      style={{
                        left: `${sectionPlayheadPosition}%`,
                        transition: isDraggingRef.current[scriptIndex] ? 'none' : 'left 0.008s linear'
                      }}
                      onMouseDown={isActiveSection ? handlePlayheadMouseDown : undefined}
                      onMouseEnter={handlePlayheadMouseEnter}
                      onMouseLeave={handlePlayheadMouseLeave}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        left: '-7px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: getPrimaryColor(),
                        border: '2px solid white',
                        boxShadow: `0 0 8px ${getPrimaryColorLight()}`,
                        transform: isPlayheadHovered ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.15s ease'
                      }} />
                    </div>
                  )}

                  <div className="captions-track">
                    {wordsWithinDuration.map((word: Word, idx: number) => {
                      const startTime = (sectionPosition / 1000) + word.start;
                      const endTime = (sectionPosition / 1000) + word.end;
                      const isActive = isCaptionActive(word, sectionPosition, totalSectionDuration);
                      const isEditing = editingCaption?.index === idx && editingCaption?.sectionIndex === parseInt(scriptIndex);
                      const isDraggingThisCaption = captionDragState?.index === idx &&
                        captionDragState?.sectionIndex === parseInt(scriptIndex) &&
                        captionDragState?.isDragging;

                      const captionPosition = {
                        left: `${(word.start / (totalSectionDuration / 1000)) * 100}%`,
                        width: `${((word.end - word.start) / (totalSectionDuration / 1000)) * 100}%`,
                      };

                      return (
                        <Popover
                          key={idx}
                          content={captionEditContent}
                          title="Edit Caption"
                          trigger={[]}
                          open={isEditing}
                          onOpenChange={(visible) => {
                            if (!visible && isEditing) {
                              handleCaptionCancel();
                            }
                          }}
                          overlayStyle={{
                            pointerEvents: 'auto',
                            zIndex: 1000
                          }}
                          destroyTooltipOnHide
                          getPopupContainer={(triggerNode) => document.body}
                        >
                          <div
                            ref={(el) => {
                              captionRefs.current[`${scriptIndex}-${idx}`] = el;
                            }}
                            className={`caption-block ${isActive ? 'active-caption' : ''} ${isDraggingThisCaption ? 'dragging-caption' : ''}`}
                            style={{
                              left: captionPosition.left,
                              width: captionPosition.width,
                              backgroundColor: isActive ? `var(--color-primary-200)` : `var(--color-primary-100)`,
                              borderLeft: isActive ? `2px solid var(--color-primary-500)` : undefined,
                              cursor: isCtrlPressed ? 'pointer' : 'move',
                              zIndex: isDraggingThisCaption ? 100 : (isActive ? 2 : 1)
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              handleCaptionClick(idx, word.word, parseInt(scriptIndex));
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleCaptionDragStart(word, idx, parseInt(scriptIndex), e);
                            }}
                            title={`${word.word} (${startTime.toFixed(1)}s - ${endTime.toFixed(1)}s) | Double click to edit`}
                          >
                            <div className="caption-text" style={{
                              color: isActive ? getPrimaryColorDark() : 'rgba(0, 0, 0, 0.75)',
                              fontWeight: isActive ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none'
                            }}>
                              {word.word}
                            </div>

                            {/* Resize handles */}
                            <div
                              className="caption-resize-handle caption-resize-handle-left"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleCaptionResizeStart('left', word, idx, parseInt(scriptIndex), e);
                              }}
                            />
                            <div
                              className="caption-resize-handle caption-resize-handle-right"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleCaptionResizeStart('right', word, idx, parseInt(scriptIndex), e);
                              }}
                            />
                          </div>
                        </Popover>
                      );
                    })}
                  </div>

                  <div className="sfx-track">
                    {sectionSfx.map((sfx: SFX, idx: number) => {
                      // Calculate section start time (cumulative)
                      let sectionStartTime = 0;
                      for (let i = 0; i < parseInt(scriptIndex); i++) {
                        const prevSectionPages = groupPagesByScriptIndex(jsonPages.pages)[i] || [];
                        sectionStartTime += prevSectionPages.reduce((sum: number, page: any) => sum + page.duration, 0) / 1000;
                      }

                      // Calculate relative position within this section
                      const relativeStart = sfx.start - sectionStartTime;
                      const relativeEnd = sfx.end - sectionStartTime;

                      const sfxStartPercent = (relativeStart / (sectionDuration / 1000)) * 100;
                      const sfxDurationPercent = ((relativeEnd - relativeStart) / (sectionDuration / 1000)) * 100;

                      const isActive = currentTimeMs >= startTime + (relativeStart * 1000) &&
                        currentTimeMs <= startTime + (relativeEnd * 1000);
                      const isDraggingThisSfx = sfxDragState?.sfx === sfx && sfxDragState?.isDragging;

                      return (
                        <div
                          key={`sfx-${idx}`}
                          ref={(el) => {
                            sfxRefs.current[`${scriptIndex}-${idx}`] = el;
                          }}
                          className={`sfx-block ${isActive ? 'active-sfx' : ''} ${isDraggingThisSfx ? 'dragging' : ''}`}
                          style={{
                            left: `${sfxStartPercent}%`,
                            width: `${sfxDurationPercent}%`,
                            zIndex: isDraggingThisSfx ? 1000 : (isActive ? 2 : 1)
                          }}
                          onMouseDown={(e) => {
                            // Only start drag if not clicking controls
                            if (!(e.target as HTMLElement).closest('.sfx-controls')) {
                              handleSfxDragStart(sfx, idx, parseInt(scriptIndex), e);
                            }
                          }}
                        >
                          <Tooltip
                            title={
                              <div>
                                <div style={{ fontWeight: 500, marginBottom: 2 }}>{sfx.name}</div>
                                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                  Start: {sfx.start.toFixed(2)}s
                                  <br />
                                  End: {sfx.end.toFixed(2)}s
                                  <br />
                                  Duration: {(sfx.end - sfx.start).toFixed(2)}s
                                </div>
                              </div>
                            }
                            mouseEnterDelay={0.5}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <div className="sfx-icon">
                                <ThunderboltFilled />
                              </div>
                              <div className="sfx-text">
                                {sfx.name}
                              </div>
                              {sfx?.id && (
                                <div className="sfx-controls" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                  <Tooltip title="Duplicate SFX" mouseEnterDelay={0.5}>
                                    <div
                                      className="sfx-control-button"
                                      onClick={(e) => {
                                        handleSfxDuplicate(sfx);
                                      }}
                                    >
                                      <CopyOutlined />
                                    </div>
                                  </Tooltip>
                                  <Tooltip title={sfx.volume === 0 ? "Unmute" : "Mute"} mouseEnterDelay={0.5}>
                                    <div
                                      className={`sfx-control-button ${sfx.volume === 0 ? 'muted' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSfxMuteToggle(sfx.id);
                                      }}
                                    >
                                      {sfx.volume === 0 ? <AudioMutedOutlined /> : <SoundOutlined />}
                                    </div>
                                  </Tooltip>
                                  <Tooltip title="Delete SFX" mouseEnterDelay={0.5}>
                                    <div
                                      className="sfx-control-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSfxDelete(sfx.id);
                                      }}
                                      style={{
                                        backgroundColor: 'var(--color-error-100)',
                                        borderColor: 'var(--color-error-200)',
                                        color: 'var(--color-error-600)'
                                      }}
                                    >
                                      <DeleteOutlined />
                                    </div>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                          <div className="sfx-wave"></div>

                          <div
                            className="sfx-resize-handle sfx-resize-handle-left"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSfxResizeStart('left', sfx, idx, parseInt(scriptIndex), e);
                            }}
                          />
                          <div
                            className="sfx-resize-handle sfx-resize-handle-right"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSfxResizeStart('right', sfx, idx, parseInt(scriptIndex), e);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default observer(TimelineTab);